import React, { useMemo, useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { AvailableFilter } from "@/models/filter";
import { organizeFieldsByPriority } from "@/utils/normalized-model-fields";
import { getDisplayName, isPrefixedField } from "@/utils/fields-mapping";
import FieldItem from "@/features/filters/filter-content/field-item";
import { Z_INDEX } from "@/utils/z-index";
import Switch from "@/components/ui/switch";

interface FieldNode {
  name: string;
  fullPath: string;
  children: Map<string, FieldNode>;
  field?: AvailableFilter;
}

/**
 * Construit un arbre hiérarchique à partir des champs
 */
function buildFieldTree(fields: AvailableFilter[]): Map<string, FieldNode> {
  const root = new Map<string, FieldNode>();

  fields.forEach((field) => {
    const displayName = isPrefixedField(field.key)
      ? getDisplayName(field.key)
      : field.key;
    const parts = displayName.split(".");
    let currentLevel = root;

    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      const isLeaf = i === parts.length - 1;

      if (!currentLevel.has(part)) {
        currentLevel.set(part, {
          name: part,
          fullPath: parts.slice(0, i + 1).join("."),
          children: new Map(),
          field: isLeaf ? field : undefined,
        });
      }

      const node = currentLevel.get(part)!;

      if (isLeaf) {
        node.field = field;
      }

      currentLevel = node.children;
    }
  });

  return root;
}

/**
 * Trie récursivement les nœuds de l'arbre
 */
function sortFieldTree(nodes: Map<string, FieldNode>): Map<string, FieldNode> {
  const sorted = new Map<string, FieldNode>();
  const entries = Array.from(nodes.entries()).sort(([a], [b]) =>
    a.localeCompare(b)
  );

  entries.forEach(([key, node]) => {
    sorted.set(key, {
      ...node,
      children: sortFieldTree(node.children),
    });
  });

  return sorted;
}

/**
 * Collecte tous les chemins des nœuds qui ont des enfants (pour les développer par défaut)
 */
function collectAllNodePaths(nodes: Map<string, FieldNode>): string[] {
  const paths: string[] = [];
  
  nodes.forEach((node) => {
    if (node.children.size > 0) {
      paths.push(node.fullPath);
      paths.push(...collectAllNodePaths(node.children));
    }
  });
  
  return paths;
}

/**
 * Composant pour un nœud de l'arbre avec fonctionnalité de collapse/expand
 */
const TreeNode: React.FC<{
  node: FieldNode;
  level: number;
  expandedNodes: Set<string>;
  onToggle: (path: string) => void;
  onFieldSelect?: (field: AvailableFilter) => void;
  highlightedKey?: string;
  searchQuery?: string;
}> = ({
  node,
  level,
  expandedNodes,
  onToggle,
  onFieldSelect,
  highlightedKey,
  searchQuery,
}) => {
  const hasChildren = node.children.size > 0;
  const isExpanded = expandedNodes.has(node.fullPath);
  const indent = level * 20;

  if (hasChildren) {
    const groupDisplayName = node.name;

    return (
      <React.Fragment key={node.fullPath}>
        <div
          className="flex items-center justify-between w-full px-3 py-2 cursor-pointer hover:bg-surface-secondary transition-colors"
          style={{ paddingLeft: `${indent + 12}px` }}
          onClick={(e) => {
            e.stopPropagation();
            onToggle(node.fullPath);
          }}
        >
          <span className="text-sm font-medium text-content-secondary">
            {groupDisplayName}
          </span>
          <span className="text-xs text-content-tertiary ml-2">
            {isExpanded ? "▼" : "▶"}
          </span>
        </div>

        {isExpanded &&
          Array.from(node.children.entries()).map(([key, childNode]) => (
            <TreeNode
              key={childNode.fullPath}
              node={childNode}
              level={level + 1}
              expandedNodes={expandedNodes}
              onToggle={onToggle}
              onFieldSelect={onFieldSelect}
              highlightedKey={highlightedKey}
              searchQuery={searchQuery}
            />
          ))}
      </React.Fragment>
    );
  } else if (node.field) {
    const isHighlighted = highlightedKey === node.field.key;

    return (
      <div
        key={node.field.key}
        data-field-key={node.field.key}
        className={`
          flex items-center w-full px-3 py-2 cursor-pointer transition-colors
          ${
            isHighlighted
              ? "bg-accent/10 text-accent"
              : "hover:bg-surface-secondary text-content-primary"
          }
        `}
        style={{ paddingLeft: `${indent + 12}px` }}
        onClick={(e) => {
          e.stopPropagation();
          if (onFieldSelect) {
            onFieldSelect(node.field!);
          }
        }}
      >
        <FieldItem field={node.field.key} type={node.field.type} />
      </div>
    );
  }

  return null;
};

/**
 * Composant pour une liste plate de champs (utilisé pour la recherche)
 */
const FlatFieldList: React.FC<{
  fields: AvailableFilter[];
  onFieldSelect?: (field: AvailableFilter) => void;
  highlightedKey?: string;
}> = ({ fields, onFieldSelect, highlightedKey }) => {
  return (
    <>
      {fields.map((field) => {
        const isHighlighted = highlightedKey === field.key;

        return (
          <div
            key={field.key}
            data-field-key={field.key}
            className={`
              flex items-center w-full px-3 py-2 cursor-pointer transition-colors
              ${
                isHighlighted
                  ? "bg-accent/10 text-accent"
                  : "hover:bg-surface-secondary text-content-primary"
              }
            `}
            onClick={(e) => {
              e.stopPropagation();
              if (onFieldSelect) {
                onFieldSelect(field);
              }
            }}
          >
            <FieldItem field={field.key} type={field.type} />
          </div>
        );
      })}
    </>
  );
};

/**
 * Vérifie si un champ a des données sur Elasticsearch
 * Un champ a des données s'il a des options (termes) ou des min/max (range)
 */
const fieldHasData = (field: AvailableFilter): boolean => {
  // Champ avec des options (termes) qui ont des données
  if (field.options && field.options.length > 0) {
    return field.options.some((opt) => opt.docCount > 0);
  }
  // Champ avec min/max défini (range)
  if (field.min !== undefined || field.max !== undefined) {
    return true;
  }
  return false;
};

export interface FieldDropdownProps {
  /** Liste des champs disponibles */
  availableFields: AvailableFilter[];
  /** Position du dropdown (fixed position) */
  position: { top: number; left: number };
  /** Largeur du dropdown (optionnel) */
  width?: number | string;
  /** Hauteur max du dropdown */
  maxHeight?: number;
  /** Callback quand un champ est sélectionné */
  onFieldSelect: (field: AvailableFilter) => void;
  /** Callback pour fermer le dropdown */
  onClose: () => void;
  /** Clé du champ actuellement surligné (pour navigation clavier) */
  highlightedKey?: string;
  /** Requête de recherche pour filtrer les champs */
  searchQuery?: string;
  /** Filtrer par types de champs */
  filterByTypes?: string[];
  /** Référence externe pour le dropdown */
  dropdownRef?: React.RefObject<HTMLDivElement>;
  /** Afficher le switch pour filtrer les champs avec données (défaut: true) */
  showDataFilter?: boolean;
}

/**
 * Composant de dropdown réutilisable pour la sélection de champs
 * Utilisé à la fois dans FieldSelector et LuceneTextarea
 */
export const FieldDropdown: React.FC<FieldDropdownProps> = ({
  availableFields,
  position,
  width = 300,
  maxHeight = 400,
  onFieldSelect,
  onClose,
  highlightedKey,
  searchQuery,
  filterByTypes,
  dropdownRef,
  showDataFilter = true,
}) => {
  const internalRef = useRef<HTMLDivElement>(null);
  const ref = dropdownRef || internalRef;

  // État pour gérer les nœuds expandés/collapsés
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  // État pour filtrer uniquement les champs avec données
  const [onlyWithData, setOnlyWithData] = useState(false);

  // Filtrer les champs par types si nécessaire
  const filteredFields = useMemo(() => {
    let fields = availableFields;

    if (filterByTypes && filterByTypes.length > 0) {
      fields = fields.filter((field) => filterByTypes.includes(field.type));
    }

    // Filtrer par recherche
    if (searchQuery) {
      const lowerQuery = searchQuery.toLowerCase();
      fields = fields.filter((field) =>
        field.key.toLowerCase().includes(lowerQuery)
      );
    }

    // Filtrer par champs avec données sur ES
    if (onlyWithData) {
      fields = fields.filter((field) => fieldHasData(field));
    }

    return fields;
  }, [availableFields, filterByTypes, searchQuery, onlyWithData]);

  // Organiser les champs en prioritaires et autres
  const { priorityFields, otherFields } = useMemo(() => {
    return organizeFieldsByPriority(filteredFields);
  }, [filteredFields]);

  // Construire les arbres hiérarchiques
  const priorityTree = useMemo(() => {
    const tree = buildFieldTree(priorityFields);
    return sortFieldTree(tree);
  }, [priorityFields]);

  const otherTree = useMemo(() => {
    const tree = buildFieldTree(otherFields);
    return sortFieldTree(tree);
  }, [otherFields]);

  // Collecter tous les chemins des nœuds pour les développer par défaut
  const allNodePaths = useMemo(() => {
    return new Set([
      ...collectAllNodePaths(priorityTree),
      ...collectAllNodePaths(otherTree),
    ]);
  }, [priorityTree, otherTree]);

  // Initialiser expandedNodes avec tous les chemins quand les arbres changent
  useEffect(() => {
    if (allNodePaths.size > 0 && expandedNodes.size === 0) {
      setExpandedNodes(allNodePaths);
    }
  }, [allNodePaths]);

  // Fonction pour toggle l'état d'un nœud
  const toggleNode = (path: string) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(path)) {
        newSet.delete(path);
      } else {
        newSet.add(path);
      }
      return newSet;
    });
  };

  // Scroll le champ surligné dans la vue
  useEffect(() => {
    if (highlightedKey && ref.current) {
      const highlightedEl = ref.current.querySelector(
        `[data-field-key="${highlightedKey}"]`
      );
      if (highlightedEl) {
        highlightedEl.scrollIntoView({ block: "nearest" });
      }
    }
  }, [highlightedKey, ref]);

  // Fermer sur clic à l'extérieur
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        onClose();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose, ref]);

  // Si recherche active, afficher une liste plate
  const showFlatList = searchQuery && searchQuery.length > 0;

  const dropdownContent = (
    <div
      ref={ref}
      className="bg-surface-elevated border border-arc-border rounded-arc-md shadow-arc-lg overflow-hidden animate-fadeIn flex flex-col"
      style={{
        position: "fixed",
        top: position.top,
        left: position.left,
        width: typeof width === "number" ? width : width,
        maxHeight,
        zIndex: Z_INDEX.POPOVER,
      }}
    >
      {/* Switch pour filtrer les champs avec données */}
      {showDataFilter && (
        <div 
          className="flex items-center justify-between px-3 py-2 border-b border-arc-border bg-surface-secondary/50 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Switch
            size="sm"
            checked={onlyWithData}
            onChange={setOnlyWithData}
            label="Avec données uniquement"
            labelPosition="right"
          />
        </div>
      )}

      {/* Liste des champs scrollable */}
      <div className="overflow-y-auto flex-1">
      {filteredFields.length === 0 ? (
        <div className="px-3 py-4 text-sm text-content-tertiary text-center">
          {onlyWithData ? "Aucun champ avec données" : "Aucun champ trouvé"}
        </div>
      ) : showFlatList ? (
        <FlatFieldList
          fields={filteredFields}
          onFieldSelect={onFieldSelect}
          highlightedKey={highlightedKey}
        />
      ) : (
        <>
          {/* Section des champs prioritaires avec hiérarchie */}
          {priorityFields.length > 0 &&
            Array.from(priorityTree.entries()).map(([key, node]) => (
              <TreeNode
                key={node.fullPath}
                node={node}
                level={0}
                expandedNodes={expandedNodes}
                onToggle={toggleNode}
                onFieldSelect={onFieldSelect}
                highlightedKey={highlightedKey}
                searchQuery={searchQuery}
              />
            ))}

          {/* Section des autres champs avec hiérarchie */}
          {otherFields.length > 0 &&
            Array.from(otherTree.entries()).map(([key, node]) => (
              <TreeNode
                key={node.fullPath}
                node={node}
                level={0}
                expandedNodes={expandedNodes}
                onToggle={toggleNode}
                onFieldSelect={onFieldSelect}
                highlightedKey={highlightedKey}
                searchQuery={searchQuery}
              />
            ))}
        </>
      )}
      </div>
    </div>
  );

  // Utiliser un portal pour rendre le dropdown au niveau du body
  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(dropdownContent, document.body);
};

export default FieldDropdown;
